import { useState } from "react";
import { AlertTriangle, Bell, CheckCircle, XCircle, Eye, Zap, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  critical: { color: "#FF3B30", bg: "rgba(255,59,48,0.08)", border: "rgba(255,59,48,0.2)", icon: XCircle },
  warning: { color: "#FFCC00", bg: "rgba(255,204,0,0.06)", border: "rgba(255,204,0,0.15)", icon: AlertTriangle },
  info: { color: "#007AFF", bg: "rgba(0,122,255,0.06)", border: "rgba(0,122,255,0.15)", icon: Eye },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function AlertsPanel({ alerts, onResolve, onRunAnalysis, analysisLoading }) {
  const [expanded, setExpanded] = useState(true);

  const critical = alerts.filter(a => a.severity === "critical");
  const warning = alerts.filter(a => a.severity === "warning");
  const info = alerts.filter(a => a.severity === "info");

  return (
    <div data-testid="alerts-panel" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div className="panel-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Bell size={10} strokeWidth={1.5} style={{ color: critical.length > 0 ? "#FF3B30" : "#FFCC00" }} />
          <span className="panel-title">CENTRO DE ALERTAS</span>
          {critical.length > 0 && (
            <span className="font-mono" style={{
              fontSize: 9, background: "rgba(255,59,48,0.15)", color: "#FF3B30",
              padding: "1px 6px", borderRadius: 2, border: "1px solid rgba(255,59,48,0.3)"
            }}>
              {critical.length} CRITICAS
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <span className="font-mono" style={{ fontSize: 9, color: "#71717a" }}>
            {alerts.length} ACTIVAS
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: "none", border: "none", color: "#71717a", cursor: "pointer", padding: 0 }}
            data-testid="alerts-toggle"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>

      <div style={{
        display: "flex", gap: 4, marginBottom: 8,
        flexShrink: 0, flexWrap: "wrap"
      }}>
        <button
          data-testid="run-territory-analysis"
          onClick={() => onRunAnalysis("territories")}
          disabled={analysisLoading}
          style={{
            flex: 1, padding: "6px 6px",
            background: analysisLoading ? "rgba(255,255,255,0.03)" : "rgba(0,122,255,0.08)",
            border: "1px solid rgba(0,122,255,0.2)",
            borderRadius: 2, cursor: analysisLoading ? "wait" : "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
            color: "#007AFF", letterSpacing: "0.05em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
            transition: "all 0.15s ease"
          }}
        >
          <Zap size={9} strokeWidth={1.5} />
          {analysisLoading ? "..." : "SCOUT"}
        </button>
        <button
          data-testid="run-strategic-analysis"
          onClick={() => onRunAnalysis("strategy")}
          disabled={analysisLoading}
          style={{
            flex: 1, padding: "6px 6px",
            background: analysisLoading ? "rgba(255,255,255,0.03)" : "rgba(138,43,226,0.08)",
            border: "1px solid rgba(138,43,226,0.2)",
            borderRadius: 2, cursor: analysisLoading ? "wait" : "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
            color: "#8A2BE2", letterSpacing: "0.05em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
            transition: "all 0.15s ease"
          }}
        >
          <Zap size={9} strokeWidth={1.5} />
          {analysisLoading ? "..." : "CEO"}
        </button>
        <button
          data-testid="run-geo-intelligence"
          onClick={() => onRunAnalysis("geo")}
          disabled={analysisLoading}
          style={{
            flex: 1, padding: "6px 6px",
            background: analysisLoading ? "rgba(255,255,255,0.03)" : "rgba(52,199,89,0.08)",
            border: "1px solid rgba(52,199,89,0.2)",
            borderRadius: 2, cursor: analysisLoading ? "wait" : "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
            color: "#34C759", letterSpacing: "0.05em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
            transition: "all 0.15s ease"
          }}
        >
          <Zap size={9} strokeWidth={1.5} />
          {analysisLoading ? "..." : "GEO"}
        </button>
      </div>

      {expanded && (
        <div className="panel-body" style={{ flex: 1 }}>
          {alerts.length === 0 ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", height: "100%", gap: 8
            }}>
              <CheckCircle size={20} strokeWidth={1.5} style={{ color: "#34C759" }} />
              <span className="font-mono" style={{ fontSize: 10, color: "#71717a" }}>
                SIN ALERTAS — SISTEMA OPERANDO NORMAL
              </span>
              <span className="font-mono" style={{ fontSize: 9, color: "#52525b" }}>
                Ejecuta un analisis IA para generar alertas
              </span>
            </div>
          ) : (
            [...critical, ...warning, ...info].map((alert, i) => {
              const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
              const Icon = config.icon;

              return (
                <div key={alert.id || i} data-testid={`alert-item-${i}`} style={{
                  padding: "8px 10px",
                  marginBottom: 4,
                  background: config.bg,
                  border: `1px solid ${config.border}`,
                  borderRadius: 2,
                  transition: "background 0.15s ease"
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <Icon size={12} strokeWidth={1.5} style={{ color: config.color, flexShrink: 0, marginTop: 1 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span className="font-mono" style={{ fontSize: 9, color: config.color, letterSpacing: "0.05em" }}>
                          {alert.source || alert.type?.toUpperCase() || "ALERT"}
                        </span>
                        <span className="font-mono" style={{ fontSize: 8, color: "#52525b" }}>
                          {timeAgo(alert.timestamp)}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "#d4d4d8", marginTop: 2, lineHeight: 1.4 }}>
                        {alert.title || alert.message}
                      </div>
                      {alert.message && alert.title && alert.message !== alert.title && (
                        <div className="font-mono" style={{ fontSize: 9, color: "#71717a", marginTop: 2 }}>
                          {alert.message}
                        </div>
                      )}
                      {(alert.metric_value !== undefined && alert.metric_value !== 0) && (
                        <div className="font-mono" style={{ fontSize: 9, color: config.color, marginTop: 3 }}>
                          {alert.metric_name || "VALOR"}: {typeof alert.metric_value === 'number' ? alert.metric_value.toFixed(2) : alert.metric_value}
                          {alert.threshold ? ` / UMBRAL: ${alert.threshold}` : ""}
                        </div>
                      )}
                      {alert.suggested_action && (
                        <div className="font-mono" style={{
                          fontSize: 9, color: "#a1a1aa", marginTop: 3,
                          padding: "3px 6px", background: "rgba(255,255,255,0.03)",
                          borderRadius: 2
                        }}>
                          ACCION: {alert.suggested_action}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        {alert.requires_attention && (
                          <span className="font-mono" style={{
                            fontSize: 8, color: "#FF3B30", letterSpacing: "0.1em",
                            padding: "1px 4px", background: "rgba(255,59,48,0.1)",
                            borderRadius: 1
                          }}>
                            REQUIERE ATENCION
                          </span>
                        )}
                        <button
                          data-testid={`resolve-alert-${i}`}
                          onClick={() => onResolve(alert.id)}
                          style={{
                            background: "none", border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 2, padding: "1px 6px",
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                            color: "#71717a", cursor: "pointer", marginLeft: "auto",
                            transition: "all 0.15s ease"
                          }}
                        >
                          RESOLVER
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
